import React from 'react';
import { STRRoom } from './types';
import { formatDate, getChannelBg } from './utils';
import { useResponsive } from '../hooks/useResponsive';

interface Props {
  room: STRRoom | null;
  year: number;
  month: number;
  selStart: Date | null;
  selEnd: Date | null;
  onClickDate: (dateStr: string) => void;
  onNav: (delta: number) => void;
}

function isBlocked(dateStr: string, room: STRRoom | null): boolean {
  if (!room) return false;
  return (room.blocked_dates || []).some((bl) => dateStr >= bl.start && dateStr <= bl.end);
}

function isInSelection(dt: Date, selStart: Date | null, selEnd: Date | null): boolean {
  if (!selStart || !selEnd) return false;
  return dt >= selStart && dt <= selEnd;
}

function renderMonthGrid(
  year: number,
  month: number,
  room: STRRoom | null,
  selStart: Date | null,
  selEnd: Date | null,
  onClickDate: (ds: string) => void
) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const firstDay = new Date(year, month, 1);
  const startDow = firstDay.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const dows = ['일', '월', '화', '수', '목', '금', '토'];
  const cells: React.ReactNode[] = [];

  dows.forEach((d, i) => {
    cells.push(
      <div key={`dow-${i}`} style={{
        textAlign: 'center', fontSize: 11, fontWeight: 600, padding: '6px 0',
        color: i === 0 ? '#e53935' : i === 6 ? '#1565c0' : '#888',
      }}>
        {d}
      </div>
    );
  });

  for (let i = 0; i < startDow; i++) {
    cells.push(<div key={`empty-${i}`} style={{ minHeight: 40 }} />);
  }

  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(year, month, d);
    dt.setHours(0, 0, 0, 0);
    const dateStr = formatDate(dt);
    const isPast = dt < today;
    const isToday = dt.getTime() === today.getTime();

    let bookingsForDate: typeof room extends null ? never : NonNullable<typeof room>['bookings'] = [];
    let isCheckIn = false, isCheckOut = false;
    let checkInBooking: any = null, checkOutBooking: any = null;

    if (room) {
      (room.bookings || []).forEach((b) => {
        if (b.status === 'cancelled') return;
        if (dateStr >= b.check_in && dateStr <= b.check_out) bookingsForDate.push(b);
        if (dateStr === b.check_in) { isCheckIn = true; checkInBooking = b; }
        if (dateStr === b.check_out) { isCheckOut = true; checkOutBooking = b; }
      });
    }

    let bgStyle: React.CSSProperties = {};
    const isBlockedDate = isBlocked(dateStr, room);
    const hasBooking = bookingsForDate.length > 0;

    if (hasBooking) {
      if (isCheckOut && isCheckIn && checkOutBooking && checkInBooking && checkOutBooking.id !== checkInBooking.id) {
        bgStyle.background = `linear-gradient(to bottom, ${getChannelBg(checkOutBooking.channel)} 50%, ${getChannelBg(checkInBooking.channel)} 50%)`;
      } else {
        const mainB = checkInBooking || bookingsForDate[0];
        bgStyle.background = getChannelBg(mainB.channel);
      }
    } else if (isBlockedDate) {
      bgStyle.background = 'repeating-linear-gradient(45deg, #f5f5f5, #f5f5f5 3px, #e0e0e0 3px, #e0e0e0 4px)';
    }

    const isOpStart = room?.operation_start && dateStr === room.operation_start;
    const inRange = isInSelection(dt, selStart, selEnd);
    const isSelStart = selStart && formatDate(selStart) === dateStr;
    const isSelEnd = selEnd && formatDate(selEnd) === dateStr;

    let label = '';
    const isExtCheckIn = checkInBooking && checkInBooking.booking_type === '직거래 연장';
    const inLabel = isExtCheckIn ? '연장일' : '입주일';
    if (isOpStart && isCheckIn) label = '운영/' + inLabel;
    else if (isOpStart) label = '운영시작';
    else if (isCheckIn && isCheckOut) label = '퇴거/' + inLabel;
    else if (isCheckIn) label = inLabel;
    else if (isCheckOut) label = '퇴거일';

    cells.push(
      <div
        key={dateStr}
        onClick={() => onClickDate(dateStr)}
        style={{
          ...bgStyle,
          ...((isSelStart || isSelEnd) ? { background: '#1976d2', color: '#fff' } : {}),
          ...(inRange && !isSelStart && !isSelEnd ? { background: '#bbdefb', color: '#1565c0' } : {}),
          minHeight: 40,
          padding: 2,
          cursor: 'pointer',
          color: isPast ? '#aaa' : (isSelStart || isSelEnd) ? '#fff' : '#333',
          borderRadius: 4,
          position: 'relative',
          textAlign: 'center',
          fontSize: 12,
          transition: 'all 0.1s',
        }}
      >
        <span style={{
          display: 'block',
          lineHeight: '20px',
          fontWeight: isToday || hasBooking ? (isToday ? 700 : 500) : 400,
          textDecoration: isBlockedDate ? 'line-through' : 'none',
        }}>
          {d}
        </span>
        {label && (
          <span style={{
            display: 'block',
            fontSize: 8, lineHeight: 1,
            color: (isSelStart || isSelEnd) ? '#fff' : isOpStart ? '#2e7d32' : isCheckIn ? '#1565c0' : isCheckOut ? '#e65100' : '#666',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}>
            {label}
          </span>
        )}
      </div>
    );
  }

  // Pad to complete the grid rows
  const totalCells = startDow + daysInMonth;
  const targetCells = Math.ceil(totalCells / 7) * 7;
  for (let t = 0; t < targetCells - totalCells; t++) {
    cells.push(<div key={`pad-${t}`} style={{ minHeight: 40 }} />);
  }

  return cells;
}

export default function Calendar({ room, year, month, selStart, selEnd, onClickDate, onNav }: Props) {
  const { isMobile } = useResponsive();
  const m1 = month;
  const y1 = year;
  const m2 = month + 1 > 11 ? 0 : month + 1;
  const y2 = month + 1 > 11 ? year + 1 : year;
  const label1 = `${y1}년 ${m1 + 1}월`;
  const label2 = `${y2}년 ${m2 + 1}월`;

  // Legend — 고정 순서
  const allChannels = ['삼삼엠투', '리브애니웨어', '엔코스테이', '자리톡', '직방', '에어비앤비', '기타'];

  // 채널별 점유일수 계산 (운행일 ~ 마지막 퇴거일 기준)
  const activeBookings = (room?.bookings || []).filter(b => b.status !== 'cancelled');
  const opStart = room?.operation_start || null;
  let lastCO = opStart || '';
  activeBookings.forEach(b => { if (b.check_out && b.check_out > lastCO) lastCO = b.check_out; });
  const cumTotalDays = opStart && activeBookings.length > 0
    ? Math.max(0, Math.round((new Date(lastCO + 'T00:00:00').getTime() - new Date(opStart + 'T00:00:00').getTime()) / 86400000) + 1)
    : 0;
  const channelDays: Record<string, number> = {};
  let totalOccupied = 0;
  const occupiedDates = new Set<string>();
  activeBookings.forEach(b => {
    const ch = b.channel || '기타';
    const d = b.days || 0;
    channelDays[ch] = (channelDays[ch] || 0) + d;
    totalOccupied += d;
    // 고유 점유일 계산 (공실 산출용)
    if (b.check_in && b.check_out) {
      const ci = new Date(b.check_in + 'T00:00:00');
      const co = new Date(b.check_out + 'T00:00:00');
      const cur = new Date(ci);
      while (cur <= co) {
        const ds = formatDate(cur);
        if (opStart && ds >= opStart && ds <= lastCO) occupiedDates.add(ds);
        cur.setDate(cur.getDate() + 1);
      }
    }
  });
  const vacantDays = cumTotalDays - occupiedDates.size;
  const vacantPct = cumTotalDays > 0 ? Math.round(vacantDays / cumTotalDays * 100) : 0;

  return (
    <div>
      {/* Navigation */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <button onClick={() => onNav(-1)} style={{ background: 'none', border: '1px solid #e2e5e9', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 }}>◀</button>
        <span style={{ fontWeight: 600, fontSize: isMobile ? 13 : 14, minWidth: isMobile ? 160 : 200, textAlign: 'center' }}>{label1} — {label2}</span>
        <button onClick={() => onNav(1)} style={{ background: 'none', border: '1px solid #e2e5e9', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 }}>▶</button>
      </div>

      {/* Two month grids — side by side on desktop, stacked on mobile */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 16 : 20, alignItems: 'start' }}>
        <div style={{ flex: 1, width: '100%', height: 'fit-content' }}>
          {isMobile && <div style={{ fontSize: 12, fontWeight: 600, textAlign: 'center', marginBottom: 4, color: '#555' }}>{label1}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'auto', gridAutoRows: isMobile ? 36 : 40, alignContent: 'start', gap: isMobile ? '6px 1px' : '8px 1px' }}>
            {renderMonthGrid(y1, m1, room, selStart, selEnd, onClickDate)}
          </div>
        </div>
        <div style={{ flex: 1, width: '100%', height: 'fit-content' }}>
          {isMobile && <div style={{ fontSize: 12, fontWeight: 600, textAlign: 'center', marginBottom: 4, color: '#555' }}>{label2}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'auto', gridAutoRows: isMobile ? 36 : 40, alignContent: 'start', gap: isMobile ? '6px 1px' : '8px 1px' }}>
            {renderMonthGrid(y2, m2, room, selStart, selEnd, onClickDate)}
          </div>
        </div>
      </div>

      {/* Legend — c1 dot style */}
      <div style={{ display: 'flex', gap: isMobile ? 8 : 16, flexWrap: 'wrap', marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e5e9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888' }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: '#fff', border: '1px solid #ccc' }} />
          예약가능
        </div>
        {allChannels.map((ch) => (
          <div key={ch} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888' }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: getChannelBg(ch) }} />
            {ch}
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#888' }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: '#f5f5f5' }} />
          예약불가
        </div>
      </div>

      {/* 채널별 점유율 */}
      {cumTotalDays > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: 11, color: '#999' }}>
          <span style={{ color: '#333', fontWeight: 700 }}>환산가동률 {Math.round(totalOccupied / cumTotalDays * 100)}%</span>
          <span style={{ color: '#666', fontWeight: 600 }}>{totalOccupied}/{cumTotalDays}일<span style={{ color: '#bbb', fontWeight: 400 }}>(중복포함)</span></span>
          {allChannels.map(ch => {
            const d = channelDays[ch] || 0;
            if (d === 0) return null;
            const pct = Math.round(d / cumTotalDays * 100);
            return (
              <span key={ch} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 6, height: 6, borderRadius: 2, background: getChannelBg(ch), display: 'inline-block' }} />
                {ch} {d}일({pct}%)
              </span>
            );
          })}
          {vacantDays > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#e53935' }}>
              공실 {vacantDays}일({vacantPct}%)
            </span>
          )}
        </div>
      )}
    </div>
  );
}
